import { Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { I18N_KEYS } from '@/language/keys';
import { FlexCenVer, FlexCol } from '@/UI/styles';
import { useIsHovered } from '@/hooks/useIsHovered';
import CopyBtn from '@/components/Common/ImageList/CopyBtnV2';
import { copyImage } from '@/utils/files';
import { PlaceholderImage } from './ParamsDisplayComponents';
import type { ImageInput } from './galleryParamsUtils';
import type { MediaAsset } from '@/types/api/assets';

const COPY_EVENT_TRACKING = { name: 'gallery_params_copied_ref_image', payload: {} };

const ReferenceImageThumbnail = ({ url }: { url: string }) => {
  const [imgError, setImgError] = useState(false);

  if (imgError) return <PlaceholderImage />;

  return (
    <img
      src={url}
      alt="Reference image"
      width="56px"
      height="56px"
      style={{ borderRadius: '4px', objectFit: 'cover' }}
      onError={() => setImgError(true)}
    />
  );
};

const CopyImageButton = ({ url }: { url: string }) => {
  const { t } = useTranslation();
  const handleCopy = () => {
    void copyImage({ type: 'image', url } as MediaAsset);
  };

  return (
    <CopyBtn
      onClick={handleCopy}
      icon="copy"
      size="medium"
      title={t(I18N_KEYS.NODE_IMAGE_LIST.CONTEXT_MENU.COPY_IMAGE)}
      eventTracking={COPY_EVENT_TRACKING}
    />
  );
};

const SingleImageLayout = ({ label, url }: { label: string; url: string }) => {
  const { isHovered, onMouseEnter, onMouseLeave } = useIsHovered();

  return (
    <FlexCol sx={{ gap: 0.5, pb: 1 }}>
      <FlexCenVer
        sx={{ justifyContent: 'space-between', height: '24px', py: 0.5, boxSizing: 'border-box' }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <Typography variant="body-sm-rg">{label}</Typography>
        {isHovered && <CopyImageButton url={url} />}
      </FlexCenVer>
      <ReferenceImageThumbnail url={url} />
    </FlexCol>
  );
};

const MultiImageRow = ({ url }: { url: string }) => {
  const { isHovered, onMouseEnter, onMouseLeave } = useIsHovered();

  return (
    <FlexCenVer
      sx={{ justifyContent: 'space-between', alignItems: 'flex-start' }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <ReferenceImageThumbnail url={url} />
      {isHovered && (
        <FlexCenVer sx={{ gap: 0.5 }}>
          <CopyImageButton url={url} />
        </FlexCenVer>
      )}
    </FlexCenVer>
  );
};

interface ReferenceImagesProps {
  imageInputs: ImageInput[];
  label: string;
}

const ReferenceImages = ({ imageInputs, label }: ReferenceImagesProps) => {
  if (imageInputs.length === 1) {
    return <SingleImageLayout label={label} url={imageInputs[0].thumbnailUrl || imageInputs[0].url} />;
  }

  return (
    <FlexCol sx={{ gap: 0.5, pb: 1 }}>
      <Typography variant="body-sm-rg" sx={{ py: 0.5, height: '24px', boxSizing: 'border-box' }}>
        {label}
      </Typography>
      <FlexCol sx={{ gap: 0.5 }}>
        {imageInputs.map((img) => (
          <MultiImageRow key={img.id ?? img.order} url={img.thumbnailUrl || img.url} />
        ))}
      </FlexCol>
    </FlexCol>
  );
};

export default ReferenceImages;
